import { useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, Handshake } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerContactModal({ open, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', business_name: '', type: 'retailer', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast({ title: 'Please fill name, email, phone and city' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-applications`, form);
      setDone(true);
      toast({ title: 'Application received', description: 'Our team will call you within 24 hours.' });
    } catch (err) {
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ name: '', email: '', phone: '', city: '', business_name: '', type: 'retailer', message: '' });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="partner-contact-modal" onClick={close}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-navy text-white px-6 py-5 flex items-start justify-between" style={{background: 'linear-gradient(135deg, #0B2E6F, #071E4A)'}}>
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs mb-2">
              <Handshake size={12} /> Partner enquiry
            </div>
            <h3 className="h-display text-2xl">Join Connect Near Me</h3>
            <p className="text-xs text-blue-200 mt-1">Fill in your details — we&apos;ll call you within 24 hours.</p>
          </div>
          <button onClick={close} className="text-white/80 hover:text-white p-1" data-testid="partner-modal-close-btn"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} />
            </div>
            <h4 className="h-display text-2xl">Thank you!</h4>
            <p className="text-sm text-neutral-600 mt-2">Your enquiry has been received. Our partner team will reach out soon.</p>
            <button onClick={close} className="mt-6 btn-primary" data-testid="partner-modal-done-btn">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Your name*</label>
                <input value={form.name} onChange={(e) => update('name', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="Full name" data-testid="partner-modal-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Phone*</label>
                <input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="+91 98xxxxxxxx" data-testid="partner-modal-phone-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Email*</label>
              <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="you@example.com" data-testid="partner-modal-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-neutral-700">City*</label>
                <input value={form.city} onChange={(e) => update('city', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="Your city" data-testid="partner-modal-city-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Partner type</label>
                <select value={form.type} onChange={(e) => update('type', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="partner-modal-type-select">
                  <option value="retailer">Retailer</option>
                  <option value="distributor">Distributor</option>
                  <option value="franchise">Franchise</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Business name (optional)</label>
              <input value={form.business_name} onChange={(e) => update('business_name', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="Your shop / firm" data-testid="partner-modal-business-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Message (optional)</label>
              <textarea rows={2} value={form.message} onChange={(e) => update('message', e.target.value)} className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" placeholder="Tell us about yourself" data-testid="partner-modal-message-input" />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="partner-modal-submit-btn">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : 'Submit enquiry'}
            </button>
            <p className="text-[11px] text-neutral-500 text-center">We respect your privacy. Details are used only for partner onboarding.</p>
          </form>
        )}
      </div>
    </div>
  );
}
